import React, { FC, memo, useEffect, useRef, useState } from 'react'

import { useNavigate } from 'react-router-dom'

import { getActiveRegionId } from '@sportworld/js-sdk/src/actions/region'
import {
  getSessionId,
  getSessionToken,
  setSessionData,
} from '@sportworld/js-sdk/src/actions/session'
import { useAuth } from '@sportworld/js-sdk/src/context/authContext'
import {
  getSecondsToDate,
  decodeJwtToken,
} from '@sportworld/js-sdk/src/utils/utils'

import { updateAppConfig } from '@/actions/actions'
import { updateCreditWallets } from '@/actions/creditWallets'
import { IconButton } from '@/components/IconButton'
import { LoginHeader } from '@/components/LoginHeader'
import { QrCode } from '@/components/QrCode/QrCode'
import { RefreshQr } from '@/components/QrCode/RefreshQr'
import { SessionStatusView } from '@/components/SessionStatusView/SessionStatusView'
import { headerHeight } from '@/constants/common'
import { icons } from '@/constants/icons'
import { titleData, LoginSessionType, SessionStatus } from '@/constants/login'
import { useCommonTranslation } from '@/i18n/useCommonTranslation'
import { Keys } from '@/platform/platform'
import { ApiService } from '@/service'
import useStore from '@/store/useStore'
import { idTokenType } from '@/types/servicesTypes/identityTypes'
import { formatSecondsToTime, getActualSessionType } from '@/utils/utils'

import { CheckoutSession } from './CheckoutSession'
import { LoginSession } from './LoginSession'
import { RegisterSession } from './RegisterSession'
import { SelfServiceSession } from './SelfServiceSession'
import { ButtonBack, SessionContainer } from './styles'
import { QrContainerBg, QrWrapper } from '../styles'

const AuthRaw: FC<{
  sessionType: LoginSessionType
  isPopup?: boolean
  setPopupSessionStatus?: (sessionStatus: SessionStatus) => void
  closeHandler?: (isLoginComplited?: boolean) => void
}> = ({ sessionType, isPopup, setPopupSessionStatus, closeHandler }) => {
  const { t } = useCommonTranslation()
  const navigate = useNavigate()

  const { currentOfferId, promotionData, setPromotionData } = useStore(
    (state) => state.promotion,
  )

  const [code, setCode] = useState<string>('--------')
  const [qrUrl, setUrl] = useState<string>('')
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('')
  const [remainingTime, setRemainingTime] = useState<string>('--:--')
  const [prevCodeInfoQrCodeUrl, setPrevCodeInfoQrCodeUrl] = useState<string>()

  const { updateAccessRights, setRegisteredUser } = useAuth()

  const voucherCode = promotionData?.SCOUPONCODE
  const [sessionStatus, setSessionStatus] = useState(SessionStatus.CREATED)
  const sessionPollIntervalId = useRef(0)
  const codeExpiryTimerIntervalId = useRef(0)
  const sessionExpiryTimerTimerId = useRef(0)

  const startCodeExpiryTimer = (codeExpiryDate: Date) => {
    clearInterval(codeExpiryTimerIntervalId.current)
    const codeExpiryTime = new Date(codeExpiryDate)

    let codeRemainingSeconds = getSecondsToDate(codeExpiryTime)

    codeExpiryTimerIntervalId.current = window.setInterval(() => {
      if (codeRemainingSeconds <= 1) {
        clearInterval(codeExpiryTimerIntervalId.current)
        setSessionStatus((prevState) => {
          prevState === SessionStatus.CREATED && startSession()
          return prevState
        })
      } else {
        codeRemainingSeconds = getSecondsToDate(codeExpiryTime)
        const codeFormattedTime = formatSecondsToTime(codeRemainingSeconds)
        setRemainingTime(codeFormattedTime)
      }
    }, 1000)
  }

  const startSessionExpiryTimer = (sessionExpiryDate: Date) => {
    clearTimeout(sessionExpiryTimerTimerId.current)
    const sessionExpiryTime = new Date(sessionExpiryDate)

    const sessionRemainingSeconds = getSecondsToDate(sessionExpiryTime)
    sessionExpiryTimerTimerId.current = window.setTimeout(() => {
      clearInterval(codeExpiryTimerIntervalId.current)
      setSessionStatus(SessionStatus.EXPIRED)
    }, sessionRemainingSeconds * 1000)
  }

  const startSession = async () => {
    let payload

    if (voucherCode) {
      payload = { voucherCode }
      sessionType = LoginSessionType.CHECKOUT
    } else if (currentOfferId) {
      payload = {
        purchaseItems: [
          {
            plenigoOfferId: currentOfferId,
            quantity: 1,
          },
        ],
      }
    }

    try {
      const data = await ApiService.startDeviceFlowSession(sessionType, payload)
      const codeInfo = data?.codeInfo
      const sessionExpiryTime = data?.expiryTime
      startCodeExpiryTimer(codeInfo?.expiryTime)
      startSessionExpiryTimer(sessionExpiryTime)
      setSessionData(data)
      setCode(codeInfo?.manualCode)
      setUrl(codeInfo?.url)
      setPrevCodeInfoQrCodeUrl(codeInfo?.qrCodeUrl)
      setQrCodeUrl(`${codeInfo?.qrCodeUrl}${getActualSessionType(sessionType)}`)
    } catch (error) {}
  }

  const getSessionState = async () => {
    const id = getSessionId()
    const sessionToken = getSessionToken()
    const res = await ApiService.deviceFlowSessionInfo(
      sessionType,
      id,
      sessionToken,
    )
    res?.state &&
      setSessionStatus((prevState) =>
        prevState === SessionStatus.EXPIRED ? prevState : res.state,
      )
    return res?.state || ''
  }

  const completeSession = async () => {
    const id = getSessionId()
    const sessionToken = getSessionToken()
    const res = await ApiService.deviceFlowSessionLogin(id, sessionToken)
    const idTokenDecoded = decodeJwtToken(res.idToken)
    const email = (idTokenDecoded as idTokenType)?.Email

    setRegisteredUser({ ...res, email })
    setPromotionData(null)
    updateAccessRights()
    const regionId = getActiveRegionId()
    await updateAppConfig(regionId)

    return res
  }

  const backToPrevPage = (delay: number, isLoginComplited?: boolean) => {
    if (isPopup) {
      setTimeout(() => closeHandler?.(isLoginComplited), delay)
      return
    }
    setTimeout(() => navigate(-1), delay)
  }

  const pollSessionStatus = async () => {
    clearInterval(sessionPollIntervalId.current)
    sessionPollIntervalId.current = window.setInterval(async () => {
      const state = await getSessionState()
      if (state === SessionStatus.COMPLETED) {
        try {
          clearInterval(sessionPollIntervalId.current)
          clearInterval(codeExpiryTimerIntervalId.current)
          await completeSession()
          await updateCreditWallets()
          backToPrevPage(isPopup ? 0 : 1500, true)
        } catch (error) {}
      }
      if (state === SessionStatus.CANCELLED) {
        try {
          clearInterval(sessionPollIntervalId.current)
          clearInterval(codeExpiryTimerIntervalId.current)
          backToPrevPage(2500)
        } catch (error) {}
      }
    }, 1000)
  }

  useEffect(() => {
    return () => {
      clearInterval(sessionPollIntervalId.current)
      clearInterval(codeExpiryTimerIntervalId.current)
      clearTimeout(sessionExpiryTimerTimerId.current)
    }
  }, [])

  useEffect(() => {
    if (sessionStatus === SessionStatus.CREATED) {
      startSession()
      pollSessionStatus()
    }
    setPopupSessionStatus?.(sessionStatus)
  }, [sessionStatus])

  useEffect(() => {
    prevCodeInfoQrCodeUrl &&
      setQrCodeUrl(
        `${prevCodeInfoQrCodeUrl}${getActualSessionType(sessionType)}`,
      )
  }, [sessionType, prevCodeInfoQrCodeUrl])

  const onKeyPress = (e: { keyCode: number }) => {
    if (e.keyCode == Keys.BACKSPACE || e.keyCode == Keys.BACK) {
      !isPopup && navigate(-1)
    }
  }

  useEffect(() => {
    window.addEventListener('keyup', onKeyPress)
    return () => {
      window.removeEventListener('keyup', onKeyPress)
    }
  }, [onKeyPress])

  const getHeaderData = () => {
    return sessionStatus === SessionStatus.ERROR
      ? sessionStatus
      : `${sessionStatus}_${sessionType}`
  }

  const handleRestart = () => {
    clearInterval(sessionPollIntervalId.current)
    clearInterval(codeExpiryTimerIntervalId.current)
    clearTimeout(sessionExpiryTimerTimerId.current)
    setTimeout(setSessionStatus, 1000, SessionStatus.CREATED)
  }

  return (
    <>
      {!isPopup &&
        !(sessionType === LoginSessionType.LOGIN && promotionData) && (
          <ButtonBack $dark={!!promotionData}>
            <IconButton
              iconSrc={icons.navigation.backArrow}
              onClick={() => navigate(-1)}
            />
          </ButtonBack>
        )}
      {sessionStatus === SessionStatus.CREATED && (
        <>
          {sessionType === LoginSessionType.REGISTER && (
            <RegisterSession
              promotionData={promotionData}
              code={code}
              qrUrl={qrUrl}
              qrCodeUrl={qrCodeUrl}
              remainingTime={remainingTime}
            />
          )}
          {sessionType === LoginSessionType.LOGIN && (
            <LoginSession
              promotionData={promotionData}
              code={code}
              qrUrl={qrUrl}
              qrCodeUrl={qrCodeUrl}
              remainingTime={remainingTime}
              isRegisterButtonHidden={isPopup}
            />
          )}
          {sessionType === LoginSessionType.CHECKOUT && (
            <CheckoutSession
              code={code}
              qrUrl={qrUrl}
              qrCodeUrl={qrCodeUrl}
              remainingTime={remainingTime}
            />
          )}
          {sessionType === LoginSessionType.SELF_SERVICE && (
            <SelfServiceSession
              code={code}
              qrUrl={qrUrl}
              qrCodeUrl={qrCodeUrl}
              remainingTime={remainingTime}
            />
          )}
        </>
      )}
      {sessionStatus !== SessionStatus.CREATED && (
        <SessionContainer>
          <LoginHeader
            showLogo={false}
            title={t(titleData[getHeaderData()]?.title)}
            description={t(titleData[getHeaderData()]?.description)}
            headerHeight={headerHeight}
          />
          {(isPopup && sessionStatus === SessionStatus.EXPIRED) || (
            <SessionStatusView status={sessionStatus} />
          )}
          {sessionStatus === SessionStatus.EXPIRED && (
            <QrContainerBg>
              <QrWrapper $isDisabled={sessionStatus === SessionStatus.EXPIRED}>
                <QrCode qrPath={qrCodeUrl} />
              </QrWrapper>
              <RefreshQr title={t('Reconnect')} handler={handleRestart} />
            </QrContainerBg>
          )}
        </SessionContainer>
      )}
    </>
  )
}

export const Auth = memo(AuthRaw)
